import type { AutomationInputsType, NodeContextType } from '@unifyapps/defs/types/dataSource';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { memo, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import { InterfaceModes } from '../../../../stores/InterfaceStore';
import { INFINITE_KEYS_TO_MATCH } from '../../consts';
import { useDSCMode } from '../../context/selectors';
import { useBuildTransformerParameters } from '../../hooks/useBuildTransformerParameters';
import { useComputeDataSourcePayload } from '../../hooks/useComputeDataSourcePayload';
import { useExecuteDataSourceCallbacks } from '../../hooks/useExecuteDataSourceCallbacks';
import { useGenerateQueryOptionsFromAdvancedOptions } from '../../hooks/useGenerateQueryOptionsFromAdvancedOptions';
import { useGetPathToOmitFromQueryKey } from '../../hooks/useGetPathToOmitFromQueryKey';
import { useNodeOptionsBuilder } from '../../hooks/useNodeOptionsBuilder';
import type { DataSourceWorkflowNodeProps } from '../../types';
import { DataSourcePulseObserverGate } from '../DataSourcePulseObserver/DataSourcePulseObserverGate';
import { useAddSystemParameters } from '../DataSourceWorkflowNode/hooks/useAddSystemParameters';
import useInfiniteDataSourceWorkflowNode from './useInfiniteDataSourceWorkflowNode';

function InfiniteDataSourceWorkflowNode({
  id,
  originalDataSourceId,
  requestId,
  inputs,
  options,
  context,
  lookupRequests,
  onQueryCompletion,
  name,
  callbacks,
  advancedOptions,
  setDataSourceState,
  getCurrentQueryResult,
  refetchOnPayloadChange,
  disabled,
  infinitePaginationInfo,
  transformerCode,
  dependsOn,
  dynamicPaths,
  defaultRetryConfig,
  version,
}: DataSourceWorkflowNodeProps) {
  const computedPayload = useComputeDataSourcePayload<{
    inputs: AutomationInputsType;
    context?: NodeContextType;
  }>({
    data: { inputs, context },
    refetchOnPayloadChange,
    dataSourceId: id,
    key: requestId,
    dependsOn,
    dynamicPaths,
  });

  const pathToOmitFromQueryKey = useGetPathToOmitFromQueryKey({
    dataSourceId: id,
    dynamicPaths,
    refetchOnPayloadChange,
    inputs,
  });

  const mode = useDSCMode();
  const isBuilderMode = mode === InterfaceModes.BUILDER;
  const isTransformerConfigured = DataSourceHelper.isTransformerConfigured(transformerCode);
  const transformerParameters = useBuildTransformerParameters({ transformerCode });
  const decoratedInputs = useAddSystemParameters(computedPayload);
  const queryOptions = useGenerateQueryOptionsFromAdvancedOptions({
    advancedOptions,
    defaultRetryConfig,
  });
  const nodeOptions = useNodeOptionsBuilder({ options });

  const queryResult = useInfiniteDataSourceWorkflowNode({
    payload: {
      context: computedPayload.context,
      id: originalDataSourceId,
      inputs: decoratedInputs,
      options: nodeOptions,
      lookupRequests,
      transformerParameters,
      // version, @anandabhi04 fix this later, BE reverted this change
    },
    enabled: !disabled,
    debugParams: {
      name,
      requestId,
    },
    queryOptions,
    options: {
      isTransformerConfigured,
      isBuilderMode,
      pathToOmitFromQueryKey,
    },
    infinitePaginationInfo,
  });

  useEffect(() => {
    const currentQueryResult = getCurrentQueryResult({ id, requestId });

    if (
      isEqual(
        _pick(queryResult, INFINITE_KEYS_TO_MATCH),
        _pick(currentQueryResult, INFINITE_KEYS_TO_MATCH),
      )
    ) {
      return;
    }
    setDataSourceState({
      id,
      result: _omit(queryResult, 'outputSchema'),
      requestId,
    });
  }, [getCurrentQueryResult, id, queryResult, requestId, setDataSourceState]);

  useExecuteDataSourceCallbacks({
    callbacks,
    id,
    onQueryCompletion,
    requestId,
    outputSchema: queryResult.outputSchema,
    queryResult: {
      error: queryResult.error,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
    },
    disabled,
  });

  return (
    <DataSourcePulseObserverGate
      dsId={id}
      enabled={!disabled}
      error={queryResult.error}
      isFetching={Boolean(queryResult.isFetching)}
      objects={queryResult.objects}
      requestPayload={decoratedInputs}
    />
  );
}

export default memo(InfiniteDataSourceWorkflowNode);
